import "../GlobalDebug";
import shallowEqual from "shallowequal";
import { compareAccDocKey } from "../decorators/StoreIdEquality";
import {
  getObjectStoreIdString,
  stringifyKey,
} from "../decorators/StoreIdHelper";
import { AcceleratorBatch } from "../models/AcceleratorBatch";
import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../models/AcceleratorDoc";
import { AcceleratorDocMetadata } from "../models/AcceleratorDocMetadata";
import API from "../models/API";
import Authenticator from "../models/Authenticator";
import { BatchClassConfig } from "../models/config/BatchClassConfig";
import { BatchClassMetadataData } from "../models/config/BatchClassMetadata";
import DocTypeConfig from "../models/config/DocTypeConfig";
import { Hydratable } from "../models/hydration/Hydratable";
import { HydratableFactory } from "../models/hydration/HydratableFactory";
import {
  DeleteMessages,
  LockMessages,
  ReprocessMessages,
  SaveModelMessages,
} from "../models/messages/Messages";
import Model, { ModelData, ModelStoreKey } from "../models/Model";
import { modelAlreadyExistsChecker } from "../models/ModelAlreadyExistsChecker";
import { TableModel } from "../models/TableModel";
import { AcceleratorBatchDocumentLoader } from "../services/AcceleratorBatchDocumentLoader";
import { AcceleratorDocExtractor } from "../services/AcceleratorDocExtractor";
import { AcceleratorDocHydrator } from "../services/AcceleratorDocHydrator";
import { BatchService } from "../services/BatchService";
import {
  ConfigUiVisibleDocumentsService,
  ConfigUiVisibleDocumentsServiceImpl,
} from "../services/screenlogic/ConfigUiVisibleDocumentsService";
import eventEmitter from "../state/EventEmitter";
import { UrlState } from "../state/UrlState";
import { AcceleratorBatchStore } from "../stores/impl/AcceleratorBatchStore";
import { AcceleratorDocMetadataStore } from "../stores/impl/AcceleratorDocMetadataStore";
import { AcceleratorDocModelStore } from "../stores/impl/AcceleratorDocModelStore";
import {
  AcceleratorDocStore,
  SELECT_ALL_BATCH_INSTANCES,
} from "../stores/impl/AcceleratorDocStore";
import { BatchClassConfigStore } from "../stores/impl/BatchClassConfigStore";
import {
  BatchClassMetadataStore,
  BatchClassMetadataStoreClass,
} from "../stores/impl/BatchClassMetadataStore";
import { BatchClassRoleStore } from "../stores/impl/BatchClassRoleStore";
import { RoleStore } from "../stores/impl/RoleStore";
import { BatchClassMetadata } from "../models/config/BatchClassMetadata";
import { ModelStore } from "../stores/impl/ModelStore";
import { WindowProxyStore } from "../stores/impl/WindowProxyStore";
import "../styles/buttons.css";
import "../styles/fonts.css";
import "../styles/generic.css";
import "../styles/index.css";
// import { ActionBarCategory } from '../types/actionbar/ActionBarCategory';
// import { ActionBarItem } from '../types/actionbar/ActionBarItem';
import { SnackbarProvider } from "notistack";
import { SaveModel, SaveModelResult } from "../services/model/SaveModel";
import {
  DialogBoxConfirmEvent,
  DialogBoxYesNoEvent,
} from "../types/DialogBoxEvents";
import { DocumentStatus } from "../types/DocumentStatuses";
import { CrudActionBar, Items } from "./actionbar/CrudActionBar";
import { BooleanAlert, ModelSaveAlert } from "./AppNotifications";
import { Button } from "./buttons/Button";
import { OnOffToggle } from "./buttons/OnOffToggle";
import { DocumentClassification } from "./classification/DocumentClassification";
import { CollapsiblePanel } from "./CollapsiblePanel";
import { DocModelAssociationDebugger } from "./debug/DocModelAssociationDebugger";
import { EfdDebugger, EtdDebugger } from "./debug/EfdDebugger";
import { LockDebugger } from "./debug/LockDebugger";
import DialogBoxHandler from "./DialogBoxHandler";
import { DlfEditorList } from "./dlfEditing/DlfEditorList";
import { DocumentMetadata } from "./DocumentMetadata";
import { BatchGroup } from "./documentSelector/BatchGroup";
import { DocumentSelectorItem } from "./documentSelector/DocumentSelectorItem";
import { DocumentSelectorList } from "./documentSelector/DocumentSelectorList";
import { NoBatchClasses } from "./errorScreens/NoBatchClasses";
import { HighlightProvider } from "./highlightProvider/HighlightProvider";
import { SpanHighlightProvider } from "./highlightProvider/SpanHighlightProvider";
import { DocumentImageViewer } from "./imageViewer/DocumentImageViewer";
import { BatchLockInfoTimer } from "./locking/BatchLockInfoTimer";
import { IsLocked, Lock, LockedBy, Unlock } from "./locking/ConfigAppLocks";
import { ModelLockInfoTimer } from "./locking/ModelLockInfoTimer";
import { UserDetailsStateContext } from "./login/UserDetailsProvider";
import { LoginForm } from "./LoginForm";
import { PageLoading } from "./PageLoading";
import { PageProvider } from "./pageProvider/PageProvider";
import { BatchListPanel } from "./panels/BatchListPanel";
import { ModelConfigurationViewToolTipPanel } from "./panels/ModelConfigurationViewToolTipPanel";
import { ModelNotesPanel } from "./panels/ModelNotesPanel";
import { TooltipPanel } from "./panels/TooltipPanel";
import { LicenseWarning } from "./stale/LicenseWarning";
import { PageCountWarning } from "./stale/PageCountWarning";
import { StaleAcceleratorBatch } from "./stale/StaleAcceleratorBatch";
import { StaleModel } from "./stale/StaleModel";
import { TableEditor } from "./tableEditing/TableEditor";
import { AdvancedTooltip } from "./tooltips/AdvancedTooltip";
import {
  TTClassificationPanel,
  TTDlfPanel,
  TTDlfPanelDropdown,
  TTSelectBatch,
  TTTablePanel,
  TTTablePanelDropdown,
} from "./tooltips/ConfigurationTooltips";
import { Component, PureComponent } from "react";
import { flushSync } from "react-dom";

const DEBUG: boolean = import.meta.env.VITE_DEBUG === "true";
const LOCK_TIMEOUT_MS = Math.max(
  5000,
  Number(import.meta.env.VITE_LOCK_TIMEOUT_MS), // Enforce Minimum 1 second
);

type AppState = {
  loggedIn: boolean;
  status: // Batch Class Configs are being loaded
    | "loading_bccs"
    // Batch Instances for this Batch Class Config are being loaded.
    | "loading_bis"
    // The currently selected Batch Instance(s) are being deleted.
    | "deleting_batch_instance"
    // The currently selected Batch Instance(s) are being reprocessed.
    | "reprocessing_batch_instance"
    // The currently selected Batch Instance(s) are being reprocessed.
    | "saving_models"
    // Refreshing the currently selected, stale Model.
    | "refreshing_model"
    // Batch Class Configs are currently loaded. This does not indicate that a Batch Instance(s) has been loaded
    | "loaded";
  batchClassConfigs: BatchClassMetadataData[];
  /**
   * Undefined when no BatchClassConfig is yet selected.
   * Currently, this value undefined while the status = 'loaded' is unused behaviour, but could be used for reloading
   * Batch Class Configs ?
   */
  batchClassConfig?: BatchClassConfig;
  /**
   * If this property is undefined, then no BatchInstances have been loaded.
   * If this property is defined, but empty, then no BatchInstance has been *selected*. This means that pagination or
   * search has failed to find a BatchInstance.
   *
   * A BatchInstannce in this array should not be expected to already have its AcceleratorDocs loaded.
   */
  // batchInstances?: AcceleratorBatch[];
  /**
   * If this property is undefined, then AcceleratorDocs from all AcceleratorBatches should be visible.
   * This is property is defined, it must not be empty, and must point to a valid UUID for one of the current states'
   * AcceleratorBatches
   */
  // batchInstanceFilter?: { uuid: string, id: string };
  /**
   * If this property is undefined, then no Document is selected/loaded. This could be because a Batch Instance is
   * reprocessing, being deleted, Models are saving, etc. Anytime we don't want to display any AcceleratorDoc data.
   */
  document?: Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>;
  model?: Hydratable<Model, ModelStoreKey>;
  /**
   * Store current PageIndex
   */
  // pageIndex: number;
  /**
   * Number of models saved during Model Save request
   */
  statusPromiseCount: number;
  /**
   * Number of models to be saved during a Model Save request
   */
  statusPromiseTarget: number;
  statusPromiseLabel?: string;

  hidePageCountWarning: boolean;
  hideExpirationWarning: boolean;

  /* All lock acquisitions fail when this is true */
  debug_failLockAcquisitions: boolean;
  /* Reprocess affected batches after template save when this is true */
  debug_reprocessAffectedBatches: boolean;
  /* Render debug tools when this is true. Also enables deeper debugging view for components */
  debug_showDebugTools: boolean;
  /* Highlight all Page spans on the Image Viewer */
  debug_highlightSpans: boolean;
  activeStartRuleIndex: number | null;
};

type AppProps = {
  hostname: string;
  version: string;
  isLocked: IsLocked;
  lock: Lock;
  unlock: Unlock;
  unlockAll: () => Promise<boolean>[];
  lockedBy: LockedBy;
  deleteFailedAlert: BooleanAlert;
  deleteSuccessAlert: BooleanAlert;
  reprocessFailedAlert: BooleanAlert;
  reprocessSuccessAlert: BooleanAlert;
  modelPagesFailedAlert: ModelSaveAlert;
  modelSaveSuccessAlert: ModelSaveAlert;
  modelSaveFailureAlert: ModelSaveAlert;
};
/*
 * TODO: isolate behaviour with Model changes,saves, to service. AppNew should call service directly.
 * CONFIFuiMODELservice.
 * TODO: handle excessive AcceleratorDocModelStore.getModel calls, should be transparent.
 * CONFIGuiMODELservice is the basis for this, using a HOC is gross. hook would be nice if AppNew
 * was functional.
 * TODO: transparently keeping Model correct when changing Documents/Batches/Bccs
 *  see above.
 * TODO: investigate if unmodified/no-interaction batches should be discarded to conserve memory/lookup speeds.
 * TODO: when implement above, could modified batches be stored between batch class config changes ?
 *       i wish i had metrics on how often a user names a template, doesnt save, changes bccs, and names the same
 *       template again.
 *          POSSIBLE, ACCOMODATED FOR, YAGNI
 * TODO: ActionBar logic should be isolated.
 *       this could become significantly simpler if Actionbar was more procedural and provided through a component
 *       that wrapped AppNew state. is this evil ? it feels naughty. would it break encapsulation if i only read the
 *       state ? i'm gonna say it doesn't :)
 */

const itemToPageMap: Record<Items, string> = {
  search_model: "modelSearch",
  add_model: "createModel",
  import_model: "modelImport",
  export_model: "modelExport",
  import_batch: "batchImport",
  export_batch: "batchExport",
  import_new_bcc: "newBccImport",
  import_update_bcc: "updateBccImport",
  export_bcc: "bccExport",
  bcc_settings: "batchClassConfiguration",
  role_settings: "rolePermissions",
  license_details: "licenseDetails",
  tokens: "tokens",
  batch_recycle_bin: "batchRecycleBin",
  audit_trail: "auditTrail",
};

// TODO: implement ModelHydratorCache
// TODO: implement memoize DocumentSelectorItem - remove => changeDocument call ????
class AppNew extends Component<AppProps, AppState> {
  static contextType = UserDetailsStateContext;
  declare context: React.ContextType<typeof UserDetailsStateContext>;

  iframeWindow?: WindowProxy | null = null;
  unsubscribeHashChanges?: () => void;

  acceleratorDocHydrator: AcceleratorDocHydrator;
  visibleDocumentsService: ConfigUiVisibleDocumentsService;

  prevModel: undefined | ModelData;

  /**
   * Tracks in-flight priority/collateral AcceleratorDoc extraction chains kicked off by
   * extractAcceleratorDocs() (live template-edit previews). saveModel() awaits these before
   * saving so it never reads AcceleratorDocStore/AcceleratorDocMetadataStore mid-mutation.
   */
  pendingExtractions: Promise<void>[] = [];

  constructor(props: Readonly<AppProps>) {
    super(props);

    if (this.props.hostname == null || this.props.hostname === "")
      throw new Error(
        "An API address needs to be provided on Application load",
      );

    API.setUrl(this.props.hostname);

    this.acceleratorDocHydrator = new AcceleratorDocHydrator();

    this.visibleDocumentsService = new ConfigUiVisibleDocumentsServiceImpl(
      this.acceleratorDocHydrator,
      undefined,
      [],
    );

    this.state = {
      loggedIn: false,
      status: "loading_bccs",
      batchClassConfigs: [],
      batchClassConfig: undefined,
      document: undefined,
      model: undefined,
      statusPromiseCount: 0,
      statusPromiseTarget: 0,
      /** Do not show DEBUG gated tools */
      debug_showDebugTools: DEBUG,
      /* Cause all lock acquisitions to fail early */
      debug_failLockAcquisitions: false,
      /* Reprocess affected batches on Template save */
      debug_reprocessAffectedBatches: false,
      /* Highlight a red border around each span */
      debug_highlightSpans: false,
      hidePageCountWarning: false,
      hideExpirationWarning: false,
      activeStartRuleIndex: null,
    };

    // register global function to enable debug
    window.fssdebug.accelerator.enableDebug = (val) =>
      this.setStateSync({
        debug_showDebugTools:
          val == undefined ? !this.state.debug_showDebugTools : val,
      });
    window.fssdebug.accelerator.highlightSpans = (val) =>
      this.setStateSync({
        debug_highlightSpans:
          val == undefined ? !this.state.debug_highlightSpans : val,
      });
    window.fssdebug.accelerator.reprocessAffectedBatches = (val) =>
      this.setStateSync({
        debug_reprocessAffectedBatches:
          val == undefined ? !this.state.debug_reprocessAffectedBatches : val,
      });
  }

  // FIXME: need to sync/clear out lock information when BCC is initially set and changes
  componentDidMount() {
    this.bootstrap();

    window.addEventListener("beforeunload", (e) => {
      WindowProxyStore.closeAll();
      if (
        this.visibleDocumentsService
          .getVisibleDocumentMetadata()
          .map((doc) => doc.hasModelChanged)
          .filter(Boolean).length === 0
      ) {
        // TODO: this is ugly, move it into a hook or something ?
        sessionStorage.removeItem("ACC_TABLEEDITOR_VIEW_EXTRACTED");
        return undefined;
      }
      const confirmationMessage =
        "It looks like you have been editing something. " +
        "If you leave before saving, your changes will be lost.";

      (e || window.event).returnValue = confirmationMessage; //Gecko + IE
      return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
    });

    UrlState.addOnCloseHandler(() => {
      this.props.unlockAll();
    });

    this.unsubscribeHashChanges = UrlState.subscribeToHashChanges(
      this.handleHashChange,
    );
  }

  /**
   * Only update when state changes, because ConfigAppLocks always sends new props when context state changes.
   */
  shouldComponentUpdate(nextProps: AppProps, nextState: AppState) {
    return !shallowEqual(this.state, nextState);
  }

  setStateSync: typeof this.setState = (...args) => {
    flushSync(() => {
      this.setState(...args);
    });
  };

  handleHashChange = () => {
    if (this.state.status !== "loaded") {
      return;
    }
    if (this.state.batchClassConfigs.length === 0) {
      return;
    }
    const groups = UrlState.deconstructUrlFragment(window.location.hash);
    const [batchClassId, batchInstanceId, docId] = groups;

    if (!batchClassId) {
      return;
    }

    // 1. Check if Batch Class Config changed
    const currentBcc = this.state.batchClassConfig;
    if (batchClassId !== currentBcc?.batchClassId) {
      const match = this.state.batchClassConfigs.find(
        (bcc) => bcc.batchClassId === batchClassId,
      );
      if (match) {
        this.changeBatchClassConfig(match);
      }
      return;
    }

    // 2. Check if Batch Instance changed
    const currentBatchInstanceUuid = this.visibleDocumentsService.getBatchFilter();
    const targetBatchInstanceUuid = batchInstanceId || "";
    if (targetBatchInstanceUuid !== currentBatchInstanceUuid) {
      this.changeBatchInstance(targetBatchInstanceUuid);
      return;
    }

    // 3. Check if Document changed
    const currentDocId = this.state.document?._storeId?.docId || "";
    const targetDocId = docId || "";
    if (targetDocId !== currentDocId) {
      const key = {
        batchInstanceUuid: targetBatchInstanceUuid,
        docId: targetDocId,
      };
      this.changeDocument(key);
    }
  };

  async bootstrap() {
    this.loadBatchClassConfigs(true)
      .then(() => {
        console.log("then", this.state);
        if (this.state.batchClassConfig) {
          console.log("loading batch instances");
          return this.loadBatchInstances();
        }
      })
      .then(() => {
        this.postLoadBatchInstances();
      })
      .then(() => {
        const groups = UrlState.deconstructUrlFragment(window.location.hash);
        const [, , docId] = groups;
        let keyToSelect = this.visibleDocumentsService.getAcceleratorDoc()?._storeId;
        if (docId) {
          const allDocs = this.visibleDocumentsService.getVisibleDocumentMetadata();
          const match = allDocs.find((d) => d.storeId.docId === docId);
          if (match) {
            keyToSelect = match.storeId;
          }
        }
        return this.changeDocument(
          keyToSelect ?? {
            batchInstanceUuid: "",
            docId: "",
          },
        );
      });
  }

  hideDocument() {
    this.setStateSync({
      document: undefined,
      model: undefined,
    });
  }

  async changeDocument(key: AcceleratorDocStoreKey, batchUuidFilter?: string) {
    this.visibleDocumentsService?.changeDocument(key);
    this.prevModel = undefined;
    if (batchUuidFilter) {
      this.visibleDocumentsService.setBatchFilter(batchUuidFilter);
    }

    if (this.state.batchClassConfig) {
      const bUuid = this.visibleDocumentsService.getBatchFilter() || key.batchInstanceUuid;
      const dId = key.docId;
      UrlState.updateUrlFragments({
        batchClassId: this.state.batchClassConfig.batchClassId,
        batchInstanceId: bUuid && bUuid.trim() !== "" ? bUuid : undefined,
        docId: dId && dId.trim() !== "" ? dId : undefined,
      });
    }
    // get metadata so we can guess the model key for initial loads
    const metadata = this.visibleDocumentsService.getAcceleratorDocMetadata();
    const accDocPromise = this.visibleDocumentsService
      .getAcceleratorDoc()
      ?.hydrate();
    const model = !metadata
      ? undefined
      : HydratableFactory.getModel(
          async () => {
            const doc = await accDocPromise;
            if (doc) {
              const m = AcceleratorDocModelStore.getModel(doc);
              this.prevModel = m?.toModelData();
              return m ? new Model(this.prevModel!) : undefined;
            }
            return undefined;
          },
          metadata?.guessModelStoreKey() ?? {
            batchClassIdentifier: "",
            name: "",
          },
        );

    if (accDocPromise) {
      accDocPromise
        .then(() => model!.hydrate())
        .then(() => {
          (
            this.visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
          )._wipeAcceleratorDocMetadataCache();
          this.forceUpdate();
        });
    }

    this.setStateSync({
      document: this.visibleDocumentsService?.getAcceleratorDoc(),
      model,
      status: "loaded",
    });
  }

  /**
   * Obtain a Model lock, publish a DialogBox event if the Lock could not be obtained.
   */
  async lockModel(
    modelUuid: string,
    modelKey: ModelStoreKey,
    batchUuid: string,
  ): Promise<boolean> {
    const targetModelUuid = modelKey.name === "" ? "" : modelUuid;

    // check if model or batch locked
    const isLockedAlready = await this.props.isLocked(
      targetModelUuid,
      modelKey.batchClassIdentifier,
      batchUuid,
    );
    if (isLockedAlready === "user") {
      return true;
    } else if (isLockedAlready === "none") {
      const didLock = await this.props.lock(
        targetModelUuid,
        modelKey.batchClassIdentifier,
        batchUuid,
      );
      if (!didLock) {
        eventEmitter.publish(
          new DialogBoxConfirmEvent(
            LockMessages.failedToLock(
              targetModelUuid === "" ? "Batch" : "Template",
            ),
            () => {},
            "Error",
          ),
        );
      }
      return didLock;
    } else {
      // FIXME: show someone else locked, allow admin to unlock. ideally should return whether model or batch locked ?
      const lockedObject =
        isLockedAlready === "other_batch" ? "Batch" : "Template";
      // NOTE: == true was required, looks like babel screwed up this condition
      let lockedBy = await this.props.lockedBy(targetModelUuid, batchUuid);
      if (this.context && this.context.admin) {
        const adminUnlocked = await this.adminUnlock(
          targetModelUuid,
          modelKey.batchClassIdentifier,
          batchUuid,
          lockedObject,
          lockedBy,
        );

        // if admin unlocked, immedaitely lock
        if (adminUnlocked) {
          const didLock = await this.props.lock(
            targetModelUuid,
            modelKey.batchClassIdentifier,
            batchUuid,
          );
          if (!didLock) {
            eventEmitter.publish(
              new DialogBoxConfirmEvent(
                LockMessages.failedToLock(
                  targetModelUuid === "" ? "Batch" : "Template",
                ),
                () => {},
                "Error",
              ),
            );
          }
          return didLock;
        } else {
          // don't bother showing the below "Locked by X" alert, just return false
          return false;
        }
      }

      lockedBy = await this.props.lockedBy(targetModelUuid, batchUuid);

      eventEmitter.publish(
        new DialogBoxConfirmEvent(
          LockMessages.config_objectNotLocked(lockedObject, lockedBy),
          () => {},
          "Warning",
        ),
      );

      return false;
    }
  }

  /**
   * Raise a Dialog Yes/No Event that resolves the returned Promise
   */
  adminUnlock(
    modelUuid: string,
    batchClassId: string,
    batchUuid: string,
    lockedObject: "Template" | "Batch",
    lockedBy: string | undefined,
  ): Promise<boolean> {
    return new Promise<boolean>((res) => {
      eventEmitter.publish(
        new DialogBoxYesNoEvent(
          LockMessages.config_adminUnlock(lockedObject, lockedBy),
          async (yes: boolean) => {
            if (yes) {
              // trigger unlock, return result of unlock
              const unlocked = await this.props.unlock(
                modelUuid,
                batchClassId,
                batchUuid,
              );
              res(unlocked);
            } else {
              // don't admin unlock if user hasn't requested it
              res(false);
            }
          },
          undefined,
          "Warning",
        ),
      );
    });
  }

  // TODO: test that this functions correctly, it's complex
  // TODO: modifiedModel should be a new Model object, should not mutate stored Model
  async updateModelName(
    newName: string,
    modifiedModel: Model,
    document: Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>,
  ) {
    if (!this.state.document?.isHydrated()) {
      throw new Error(
        "Cannot update Model Name when AcceleratorDoc is dehydrated.",
      );
    }

    const newModelUuid = await modelAlreadyExistsChecker(
      newName,
      modifiedModel.getBatchClassIdentifier(),
    );
    const didLock = await this.lockModel(
      newModelUuid,
      {
        name: newName,
        batchClassIdentifier: modifiedModel.getBatchClassIdentifier(),
      },
      this.visibleDocumentsService.getAcceleratorDoc()?._storeId
        .batchInstanceUuid ?? "",
    );
    if (!didLock || this.state.debug_failLockAcquisitions) {
      return;
    }
    // remove the current doc association when renaming model
    AcceleratorDocModelStore.removeAssociation(
      this.state.document.getHydrated()!.storeId,
      modifiedModel.storeId,
    );
    // change Model name
    modifiedModel.setName(newName);
    // set uuid
    modifiedModel.uuid = newModelUuid;

    this.updateModel(modifiedModel, document);
  }

  /**
   * Promise resolves once Model updated, and AcceleratorDocs extracted
   * @param modifiedModel
   * @param changed sets the value of model.changed. if false, extraction will not be performed
   * @param forceExtraction force the model to be extracted even if chagned=false. useful when setting generated reuse
   *                        values on mount
   */
  async updateModel(
    modifiedModel: Model,
    document: Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>,
    changed: boolean = true,
    forceExtraction: boolean = false,
  ) {
    if (!this.state.document?.isHydrated()) {
      throw new Error("Cannot update Model when AcceleratorDoc is dehydrated.");
    }
    // set changed model as this.state.model. will be rolledback if update fails
    this.setStateSync({
      model: HydratableFactory.getModel(
        async () => undefined,
        modifiedModel.storeId,
      ).manuallyHydrate(modifiedModel),
    });
    const didLock = await this.lockModel(
      modifiedModel.uuid ?? "",
      modifiedModel.storeId,
      document._storeId.batchInstanceUuid ?? "",
    );
    // if (modifiedModel.uuid && getLockedConfigModels()[modifiedModel.getBatchClassIdentifier()]?.includes(modifiedModel.uuid)) {
    //     didLock = true;
    // } else {
    //     didLock = await this.lockModel(modifiedModel.storeId, this.visibleDocumentsService.getAcceleratorDoc()._storeId.batchInstanceUuid ?? '');
    // }
    if (!didLock || this.state.debug_failLockAcquisitions) {
      // this.prevModel
      const newModel = new Model(this.prevModel!);
      this.setStateSync({
        model: HydratableFactory.getModel(
          async () => undefined,
          newModel.storeId,
        ).manuallyHydrate(newModel),
      });
      return this.forceUpdate();
    }
    // get DocTypeConfig for Model
    const docTypeConfig: DocTypeConfig | undefined =
      this.state.batchClassConfig?.getDocumentType(modifiedModel.docType);
    // mark model as changed
    modifiedModel.setChanged(changed);
    console.log(document._storeId);
    const setModel = AcceleratorDocModelStore.setModel(
      document.getHydrated().storeId,
      modifiedModel,
    );
    this.prevModel = setModel.toModelData();
    // AcceleratorDocModelStore.setModel() already synced classifiedModelName onto the
    // AcceleratorDocMetadata/AcceleratorDoc; hasModelChanged/classifiedModel remain ours to set.
    const docMeta = AcceleratorDocMetadataStore.getWithoutLookup(document._storeId);
    if (docMeta) {
      docMeta.setModelChanged(changed);
    }
    if (document.isHydrated()) {
      const hydratedDoc = document.getHydrated();
      hydratedDoc.hasModelChanged = changed;
      hydratedDoc.classifiedModel = setModel.toModelData();
    }
    // clone the stored model
    const hydratableModel = HydratableFactory.getModel(
      async () => undefined,
      setModel.storeId,
    ).manuallyHydrate(new Model(this.prevModel));
    this.setStateSync(
      {
        model: hydratableModel,
      },
      () => {
        if (!changed && !forceExtraction) {
          // console.debug(`Not trigger reextraction with unchanged Model ${getObjectStoreIdString(modifiedModel)}`);
          return;
        }
        try {
          // model can be assumed to be hydrated here !
          // TODO: move this method somewhere else. it might need to handle proxied AcceleratorDocs better
          const accDocsToExtract =
            AcceleratorDocModelStore.classifiedeAcceleratorDocsToExtract(
              this.state.model?._storeId as ModelStoreKey,
            );
          const collateralDocs = accDocsToExtract.filter(
            (x) =>
              !compareAccDocKey(x, document._storeId as AcceleratorDocStoreKey),
          );
          // we now have our priority and collateral AcceleratorDocs to extract. btw, this logic is clearly too
          // complex for AppNew, please extract !
          this.extractAcceleratorDocs(
            this.state.batchClassConfig!,
            this.state.model!.getHydrated(),
            // main doc to extract
            document._storeId,
            // collateral docs to extract
            collateralDocs,
          );
        } catch (e) {
          console.error("Error extracting Documents with updated Model", e);
        }
      },
    );
  }

  // TODO: update promise status targets and stuff
  extractAcceleratorDocs(
    batchClassConfig: BatchClassConfig,
    model: Model,
    priorityDoc: AcceleratorDocStoreKey,
    collateralDocs: AcceleratorDocStoreKey[],
  ) {
    const hy_priorityDoc = this.acceleratorDocHydrator.load(
      priorityDoc,
      priorityDoc.batchInstanceUuid,
      this.state.batchClassConfig!,
    );
    const hy_collaterals = collateralDocs.map((x) =>
      this.acceleratorDocHydrator.load(
        x,
        x.batchInstanceUuid,
        this.state.batchClassConfig!,
      ),
    );

    const extractDoc = (
      acceleratorDoc: AcceleratorDoc,
      model: Model,
      docTypeConfig: DocTypeConfig,
    ): [Promise<boolean>, AcceleratorDoc] => [
      new AcceleratorDocExtractor(
        acceleratorDoc,
        model,
        docTypeConfig,
        batchClassConfig.acceleratorOcrThresholds,
        // this extraction is a preview of the current user's own unsaved template
        // edit, so a 404 here doesn't mean the batch changed externally
        false,
      ).extract(),
      acceleratorDoc,
    ];

    const priorityPromise = hy_priorityDoc
      .hydrate()
      .then((doc) => {
        if (!doc) {
          throw new Error("hy_priorityDoc not hydrated properly!");
        }
        return extractDoc(
          doc,
          model,
          batchClassConfig.getDocumentType(model.docType)!,
        );
      })
      .then((output) =>
        output[0].then((x) => {
          if (!x) {
            console.warn("Extraction failed for hy_priorityDoc");
          }
          this.setDocMetadataFromDoc(
            output[1],
            AcceleratorBatchStore.getWithoutLookup(
              output[1].storeId.batchInstanceUuid,
            )?.batchInstanceName ?? "",
          );
          this.forceUpdate();
        }),
      )
      .catch((e) => {
        console.error("Error extracting priority AcceleratorDoc", e);
      });

    const collateralPromise = Promise.all(hy_collaterals.map((x) => x.hydrate()))
      .then((docs) =>
        docs.map((doc, index) => {
          if (!doc) {
            throw new Error(`hy_collateral[${index}] not hydrated properly!`);
          }
          return extractDoc(
            doc,
            model,
            batchClassConfig.getDocumentType(model.docType)!,
          );
        }),
      )
      .then((outputs) =>
        Promise.all(
          outputs.map((output, index) =>
            output[0].then((x) => {
              if (!x) {
                console.warn(`Extraction failed for hy_collateral[${index}]`);
              }
              this.setDocMetadataFromDoc(
                output[1],
                AcceleratorBatchStore.getWithoutLookup(
                  output[1].storeId.batchInstanceUuid,
                )?.batchInstanceName ?? "",
              );
              this.forceUpdate();
            }),
          ),
        ),
      )
      .catch((e) => {
        console.error("Error extracting collateral AcceleratorDocs", e);
      });

    // TODO: add "extracting" status to AccDocMetadata, or somewhere

    // Track this priority+collateral extraction chain so saveModel() can wait for it to
    // settle before reading AcceleratorDocStore/AcceleratorDocMetadataStore.
    const combined = Promise.all([priorityPromise, collateralPromise]).then(
      () => undefined,
    );
    this.pendingExtractions.push(combined);
    combined.finally(() => {
      this.pendingExtractions = this.pendingExtractions.filter(
        (p) => p !== combined,
      );
    });
  }

  // TODO: extract to service !
  setDocMetadataFromDoc(doc: AcceleratorDoc, batchName: string) {
    // merge AcceleratorDocMetadata
    let metadata = AcceleratorDocMetadataStore.getWithoutLookup(doc.storeId);
    let classifiedModelName = doc.modelName;
    if (
      classifiedModelName === "" &&
      (doc.classifiedModel?.templateType === "GENERAL" ||
        doc.classifiedModel?.templateType === "FORMAT" ||
        metadata?.wasGeneral ||
        metadata?.wasGeneralFormat)
    ) {
      classifiedModelName =
        doc.classifiedModel?.name || metadata?.classifiedModelName || "";
    }
    if (!metadata) {
      metadata = new AcceleratorDocMetadata({
        batchInstanceId: doc.batchInstanceId,
        classifiedDocType: doc.classifiedDocType,
        batchName: batchName,
        pageCount: doc.pages.length,
        classifiedModelName,
        source: doc.source,
        statuses: doc.statuses,
        uuid: doc.uuid,
        batchInstanceUuid: doc.storeId.batchInstanceUuid,
        docId: doc.storeId.docId,
        // batchClassId: doc.getBatchClassId(),
      });
      // } else {
      // merge metadata !
    }
    metadata.statuses = doc.statuses;
    metadata.classifiedDocType = doc.classifiedDocType;
    metadata.classifiedModelName = classifiedModelName;
    metadata.hasModelChanged = doc.hasModelChanged;

    AcceleratorDocMetadataStore.set(metadata, true);
    // update visibleDocumentsMetadata cache
    (
      this.visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
    )._wipeAcceleratorDocMetadataCache();
    // (this.visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl)._visibleDocumentMetadata?.find(x => compareAccDocKey(x.storeId, metadata?.storeId))
  }

  /*
   * Load all AcceleratorBatches by BatchClassId.
   */
  // TODO: extract this into either a new or existing service. I believe there were plans to have this in BatchService
  async loadBatchInstances(): Promise<undefined> {
    this.setStateSync({
      status: "loading_bis",
    });
    // return new Promise(res => {
    // TODO: reset state.status
    if (!this.state.batchClassConfig) return;
    console.log("got to here");
    const bccToLoad = new BatchClassConfig(
      this.state.batchClassConfig.toData(),
    );

    // console.time('[APPNEW]-AccBatchesLoading')
    const accBatchPromises = BatchService.loadBatches(bccToLoad, "");
    const accBatches = await accBatchPromises;
    if (
      getObjectStoreIdString(this.state.batchClassConfig) ===
      getObjectStoreIdString(bccToLoad)
    ) {
      this.visibleDocumentsService.setBatches(accBatches);
      if (accBatches.length > 0) {
        const groups = UrlState.deconstructUrlFragment(window.location.hash);
        const [, batchInstanceId] = groups;
        const exists =
          batchInstanceId === SELECT_ALL_BATCH_INSTANCES ||
          accBatches.some((b) => b.uuid === batchInstanceId);
        const initialBatchUuid = exists ? batchInstanceId : accBatches[0].uuid;
        this.visibleDocumentsService.setBatchFilter(initialBatchUuid);
      }
    }

    // console.timeEnd('[APPNEW]-AccBatchesLoading')
    return;
  }

  async loadBatchClassConfigs(skipStatusCheck = false) {
    if (!this.state.loggedIn) return;
    if (!skipStatusCheck && this.state.status === "loading_bccs") return;
    this.setStateSync({
      status: "loading_bccs",
    });
    let bccs: BatchClassMetadataData[] = [];
    // get all BatchClassConfigs

    try {
      bccs = await BatchClassMetadataStoreClass.syncAll();
    } catch (e) {
      console.error(
        "An error was encountered syncing the BatchClassConfigs",
        e,
      );
    }

    BatchClassMetadataStore.setMany(bccs, true);

    try {
      await Promise.all(
        bccs.map(async (bcc) => {
          const store = BatchClassRoleStore.getBatchClassStore(bcc);
          const bccRoles = await BatchClassMetadata.syncRoles(bcc.batchClassId);
          const bcStorePromises = store.getMany(Array.from(bccRoles));
          const rolePromises = Array.from(bccRoles).map((x) =>
            RoleStore.get(x),
          );
          await Promise.all([...bcStorePromises, ...rolePromises]);
        }),
      );
    } catch (e) {
      console.error("An error was encountered loading roles", e);
    }

    await this.newBatchClassConfigMetadatas(BatchClassMetadataStore.getAll());
  }

  /*
   * Sets the first or the filtered Batch Instance as the current Batch.
   */
  postLoadBatchInstances() {
    this.setStateSync({
      // update state from loading status
      status: "loaded",
    });
  }

  async newBatchClassConfigMetadatas(
    batchClassConfigs: BatchClassMetadataData[],
  ) {
    flushSync(() => {
      this.setStateSync({
        status: "loading_bccs",
        batchClassConfigs: batchClassConfigs,
      });
    });

    let chosenBcc: undefined | BatchClassMetadataData = undefined;
    if (batchClassConfigs.length !== 0) {
      // TODO: move url fragment stuff into its own class / helper
      chosenBcc = UrlState.filterBccs(batchClassConfigs);
      // select first bcc if missing
      if (!chosenBcc) {
        chosenBcc = batchClassConfigs[0];
      }
    }
    // FIXME: test set state to loaded, no bccs screen ?
    if (chosenBcc == undefined) {
      flushSync(() => {
        this.setStateSync({ status: "loaded" });
      });
      return null;
    }
    UrlState.updateBcc(chosenBcc);
    // sync actual BCC
    try {
      const bcc = await BatchClassConfigStore.get(chosenBcc.batchClassId);
      flushSync(() => {
        this.setStateSync({
          batchClassConfig: bcc,
          status: "loaded",
        });
      });
    } catch (err) {
      console.log("Could not load BCC", err);
    }
  }

  newBatchClassConfigs(batchClassConfigs: BatchClassConfig[]): Promise<void> {
    return new Promise((res) => {
      let batchClassConfig = undefined;
      if (batchClassConfigs.length !== 0) {
        // TODO: move url fragment stuff into its own class / helper
        batchClassConfig = UrlState.filterBccs(batchClassConfigs);
        // batchClassConfig = batchClassConfigs[0];
        if (!batchClassConfig) {
          batchClassConfig = batchClassConfigs[0];
        }
      }
      this.setStateSync(
        {
          batchClassConfig,
          batchClassConfigs: BatchClassMetadataStore.getAll(),
          status: "loaded",
        },
        () => {
          UrlState.updateBcc(this.state.batchClassConfig);
          res();
        },
      );
    });
  }

  changeBatchClassConfig(batchClassConfig: BatchClassMetadataData) {
    this.setStateSync({
      status: "loading_bccs",
    });
    // determine if any batch instances need to be loaded for this BatchClassConfig
    UrlState.updateBcc(batchClassConfig);
    // clear out old batches
    this.visibleDocumentsService?.setBatches([]);

    BatchClassConfigStore.getThenClearOthers(batchClassConfig.batchClassId)
      .catch((err) => {
        console.log("Could not look up BCC", err);
        this.setStateSync({
          status: "loaded",
          batchClassConfigs: [],
          batchClassConfig: undefined,
          document: undefined,
          model: undefined,
        });
        return undefined;
      })
      .then((bcc) => {
        this.setStateSync(
          {
            batchClassConfig: bcc,
            document: undefined,
            model: undefined,
            status: "loaded",
          },
          () => {
            this.loadBatchInstances()
              .then(() => this.postLoadBatchInstances())
              .then(() =>
                this.changeDocument(
                  this.visibleDocumentsService.getAcceleratorDoc()
                    ?._storeId ?? {
                    batchInstanceUuid: "",
                    docId: "",
                  },
                ),
              );
          },
        );
      });
  }

  /**
   * Called by <BatchInstanceFilter /> when the currently selected Batch Instance UUID has changed, or is no longer
   * available.
   * This event can be ignored if an unintentional event may be triggered by something else done by this component.
   */
  async changeBatchInstance(batchInstanceUuid: string) {
    console.log(`changeBatchInstance(${batchInstanceUuid})`);
    if (this.state.status !== "loaded") {
      return;
    }
    this.setStateSync({
      document: undefined,
      model: undefined,
      status: "loading_bis",
    });
    this.forceUpdate();

    this.visibleDocumentsService!.setBatchFilter(batchInstanceUuid);

    if (this.state.batchClassConfig) {
      const bUuid = batchInstanceUuid;
      UrlState.updateUrlFragments({
        batchClassId: this.state.batchClassConfig.batchClassId,
        batchInstanceId: bUuid && bUuid.trim() !== "" ? bUuid : undefined,
      });
    }

    // FIXME: yick x2
    new Promise((res) => {
      const batch = this.visibleDocumentsService.getVisibleBatches()[0];
      if (batch) {
        this.changeDocument(batch.metadata[0]!.storeId);
        // TODO: check this is implicitly handled by DocumentSelectorList
        // this.changeDocument(this.visibleDocumentsService.getAcceleratorDocMetadata()!.storeId)
      } else {
        this.setStateSync({
          status: "loaded",
        });
      }
    });
  }

  searchBatch(batchUuids: string[] | null, filter: string) {
    this.visibleDocumentsService.setSearchedBatches(batchUuids, filter);
    this.visibleDocumentsService.setBatchFilter(SELECT_ALL_BATCH_INSTANCES);

    const visibleBatches = this.visibleDocumentsService.getVisibleBatches();

    // if searching a new value
    if (batchUuids != null) {
      if (visibleBatches.length === 0) {
        // if nothing visbile, clear document and model data
        this.setStateSync({
          document: undefined,
          model: undefined,
        });
      } else if (
        !visibleBatches.find(
          (x) =>
            x.uuid ===
            this.visibleDocumentsService.getAcceleratorDocMetadata()?.storeId
              .batchInstanceUuid,
        )
      ) {
        // if previously selected document no longer visible, select first visible document
        this.changeDocument(
          this.visibleDocumentsService.getVisibleDocumentMetadata()[0].storeId,
        );
      } else {
        // leave selcted document
        this.forceUpdate();
      }
    } else {
      // change to first document when search term cleared
      if (visibleBatches.length > 0 && visibleBatches[0].metadata.length > 0) {
        this.changeDocument(visibleBatches[0].metadata[0].storeId);
      }
      this.forceUpdate();
    }
  }

  registerPromiseUpdates(promises: Array<Promise<any>>, label?: string) {
    this.setStateSync({
      statusPromiseLabel: label,
    });
    promises.forEach((p) =>
      p.then(() =>
        this.setStateSync((s) => ({
          statusPromiseCount: s.statusPromiseCount + 1,
        })),
      ),
    );
  }

  resetPromiseCount() {
    this.setStateSync({
      statusPromiseCount: 0,
      statusPromiseTarget: 0,
    });
  }

  _perf() {
    // console.log(`getObjectStoreId(): ${_perf._perf_getObjectStoreId() * 1000}s`);
    // console.log(`getObjectStoreIdString(): ${_perf._perf_getObjectStoreIdString() * 1000}s`);
  }

  /**
   * Delete specific batches
   */
  async deleteBatchInstances(batchUuids: string[], skipMany?: boolean) {
    if (batchUuids.length > 1 && !skipMany) {
      eventEmitter.publish(
        new DialogBoxYesNoEvent(
          DeleteMessages.deleteManyBatch(batchUuids.length),
          (confirm: boolean) => {
            if (confirm) {
              this.deleteBatchInstances(batchUuids, true);
            }
          },
          { yes: "Continue", no: "Cancel" },
          "Delete Multiple Batches",
        ),
      );
      return;
    }

    this.hideDocument();
    this.setStateSync({
      status: "deleting_batch_instance",
    });
    // should BatchInstanceStore handle deleting Batch Instances ???

    // TODO: extract
    const notLockedBatches = batchUuids.filter((x) => {
      const lockedBy = this.props.lockedBy("", x);
      const process: boolean = lockedBy
        ? lockedBy == this.context?.username
        : true;
      console.log(
        `Deleting=${process} ${x} when locked by ${lockedBy}, current user ${this.context?.username}`,
      );
      return process;
    });
    const promises = BatchService.delete(notLockedBatches);

    this.setStateSync({
      statusPromiseTarget: promises.length,
    });

    this.registerPromiseUpdates(promises, "Deleting");
    const deleteResults = await Promise.all(promises);
    this.visibleDocumentsService!.removeBatches(
      deleteResults
        .map((deleted, index) => (deleted ? notLockedBatches[index] : null))
        .filter((x: null | string): x is string => x != null),
    );
    this.visibleDocumentsService.setSearchedBatches(null);

    this.resetPromiseCount();

    // Send notification about batch reprocess status
    {
      // All batches reprocessed properly
      this.props.deleteSuccessAlert(deleteResults.filter((x) => x == true));
      // All batches failed to reprocess
      this.props.deleteFailedAlert(deleteResults.filter((x) => x == false));
    }

    // ASSUME that our currently viewed Doc has been deleted, reset to first visible Batch.
    // this.visibleDocumentsService.
    // this.visibleDocumentsService.changeDocument()

    const key = this.visibleDocumentsService?.getAcceleratorDoc()?._storeId;
    if (key) {
      this.changeDocument(key as AcceleratorDocStoreKey);
    }
    this.setStateSync({
      status: "loaded",
    });
  }

  /**
   * Reprocess specified Batches
   */
  async reprocessBatchInstances(
    batchUuids: string[],
    skipModelChangedCheck?: boolean,
    skipMany?: boolean,
  ) {
    // TODO: check unsaved changes on all batches
    const changedModelMetadata = this.visibleDocumentsService
      .getAllDocumentMetadata()
      .filter((x) => x.hasModelChanged);

    if (
      !skipModelChangedCheck &&
      changedModelMetadata.filter((doc) => doc.hasModelChanged).length > 0
    ) {
      eventEmitter.publish(
        new DialogBoxYesNoEvent(
          ReprocessMessages.unsavedChanges(),
          (confirm: boolean) => {
            if (confirm) {
              this.reprocessBatchInstances(batchUuids, true, skipMany);
            }
          },
          { yes: "Continue", no: "Cancel" },
          "Unsaved Template Changes",
        ),
      );
      return;
    }

    // TODO: add a "don't show again" option ?
    if (batchUuids.length > 1 && !skipMany) {
      eventEmitter.publish(
        new DialogBoxYesNoEvent(
          ReprocessMessages.reprocessManyBatch(batchUuids.length),
          (confirm: boolean) => {
            if (confirm) {
              this.reprocessBatchInstances(
                batchUuids,
                skipModelChangedCheck,
                true,
              );
            }
          },
          { yes: "Continue", no: "Cancel" },
          "Reprocess Multiple Batches",
        ),
      );
      return;
    }

    this.hideDocument();
    this.setStateSync({
      status: "reprocessing_batch_instance",
    });

    // check if any visible Documents have unsaved model changes
    const changedModelKeys = changedModelMetadata
      .filter((doc) => doc.hasModelChanged)
      .map((x) => x.guessModelStoreKey());
    // reset all modified models from this batch
    const removedModels = changedModelKeys.map((x) => ModelStore.remove(x));
    // directly unlock those modified models
    await Promise.all(
      removedModels
        .filter((x) => x != undefined)
        .map((x) =>
          this.props.unlock(
            x?.uuid ?? "",
            x?.getBatchClassIdentifier() ?? "",
            "",
          ),
        ),
    );
    // remove batch data from these batches
    changedModelMetadata.forEach((x) =>
      AcceleratorBatchDocumentLoader.removeBatchData(
        x.storeId.batchInstanceUuid,
      ),
    );

    // should BatchInstanceStore handle deleting Batch Instances ???
    const notLockedBatches = batchUuids
      .filter((x) => x != null && x !== "")
      .filter((x) => {
        const lockedBy = this.props.lockedBy("", x);
        const process: boolean = lockedBy
          ? lockedBy == this.context?.username
          : true;
        console.log(
          `Reprocessing=${process} ${x} when locked by ${lockedBy}, current user ${this.context?.username}`,
        );
        return process;
      });

    const batchClassId = this.state.batchClassConfig?.batchClassId;
    // we've removed the batch data of all batches with unsaved changes, now get the diff of them and not locked batches and reload. notLockedBatches will be reloaded after reprocessing

    const reloadButNoReprocessBatches = changedModelMetadata
      .filter(
        (x) =>
          batchUuids.findIndex((y) => y === x.storeId.batchInstanceUuid) === -1,
      )
      .map((x) => x.storeId.batchInstanceUuid)
      .filter((x, i, arr) => arr.indexOf(x) === i);

    const reloadButNoReprocessPromises = BatchService.loadBatches(
      reloadButNoReprocessBatches,
      batchClassId!,
    );

    const selectedBatchUuid = this.visibleDocumentsService.getBatchFilter();
    const selectedDocId =
      this.visibleDocumentsService.getAcceleratorDoc()?._storeId;
    const promises = BatchService.reprocess(notLockedBatches);

    this.setStateSync({
      statusPromiseTarget:
        promises.length + (await reloadButNoReprocessPromises).length,
    });
    notLockedBatches.map((x) => {
      this.props.unlock("", this.state.batchClassConfig?.batchClassId ?? "", x);
    });

    this.registerPromiseUpdates(promises, "Reprocessing");
    const reprocessResults = await Promise.all(promises);
    // show reprocess errors ?
    this.resetPromiseCount();

    // Send notification about batch reprocess status
    {
      // All batches reprocessed properly
      this.props.reprocessSuccessAlert(
        reprocessResults.filter((x) => x == true),
      );
      // All batches failed to reprocess
      this.props.reprocessFailedAlert(
        reprocessResults.filter((x) => x == false),
      );
    }
    // TODO: unlock batches that were reprocessed once reprocess complete
    this.setStateSync({
      document: undefined,
      status: "loading_bis",
    });

    const acceleratorBatches = await BatchService.loadBatches(
      batchUuids,
      batchClassId!,
    );
    const reloadedButNoReprocessBatches = await reloadButNoReprocessPromises;
    this.visibleDocumentsService.removeBatches(
      reloadedButNoReprocessBatches.map((x) => x.uuid),
    );
    this.visibleDocumentsService.addBatches(reloadedButNoReprocessBatches);
    this.visibleDocumentsService.setSearchedBatches(null);
    // DRY ?, same code in loadBatchInstances()
    // TODO: rely on current AcceleratorDocMetadata.storeId.batchInstanceUuuid to drive doc selected after reprocessinng
    if (this.state.batchClassConfig?.batchClassId === batchClassId) {
      this.visibleDocumentsService.removeBatches(
        acceleratorBatches.map((x) => x.uuid),
      );
      this.visibleDocumentsService.addBatches(acceleratorBatches);
      const batches = this.visibleDocumentsService.getBatches();
      // determine if we reset visible batch instance to ALL or nah
      if (selectedBatchUuid === SELECT_ALL_BATCH_INSTANCES) {
        // change to first visible doc
        this.changeDocument(
          batches[0]!.metadata[0]!.storeId,
          selectedBatchUuid,
        );
      } else if (batches.find((x) => x.uuid === selectedBatchUuid)) {
        // change to first doc in previous batch
        this.changeDocument(
          batches.find((x) => x.uuid === selectedBatchUuid)!.metadata[0]
            .storeId,
          selectedBatchUuid,
        );
      } else if (batches.length > 0) {
        // first batch, first doc
        this.changeDocument(batches[0]!.metadata[0]!.storeId);
      }
    }

    this.setStateSync({
      status: "loaded",
    });
  }

  /**
   * Reprocess a a number of AcceleratorBatches, removing existing data before reprocessing. These AccBatches are not
   * set in the VisibleDocumentService as that requires evaluating the current BatchClassConfig, so it is left to the
   * caller to implement.
   */
  async reprocessBatches(
    batchUuids: string[],
    registerPromises: boolean = false,
  ): Promise<AcceleratorBatch[]> {
    // load batches
    if (registerPromises) {
      this.setStateSync({
        statusPromiseCount: 0,
        statusPromiseTarget: batchUuids.length,
      });
    }
    // determine if locked by user
    const notLockedBatches = batchUuids.filter((x) => {
      const lockedBy = this.props.lockedBy("", x);
      const process: boolean = lockedBy
        ? lockedBy == this.context?.username
        : true;
      console.log(
        `Reprocessing=${process} ${x} when locked by ${lockedBy}, current user ${this.context?.username}`,
      );
      return process;
    });
    const reprocessPromises = BatchService.reprocess(notLockedBatches);
    if (registerPromises) {
      this.registerPromiseUpdates(reprocessPromises, "Reprocessing");
    }
    const reprocessResults = await Promise.all(reprocessPromises);

    // create list of failed reprocesses
    const failedReprocessUuids = reprocessResults.map((success, i) =>
      success ? null : notLockedBatches[i],
    );
    // how tf do we handle this

    return await this.reloadedBatches(notLockedBatches);
  }
  /**
   * Load a number of AcceleratorBatches, removing any existing data if required. These AccBatches are not set in the
   * VisibleDocumentService as that requires evaluating the current BatchClassConfig so it is left to the caller to
   * implement.
   */
  async reloadedBatches(batchUuids: string[]): Promise<AcceleratorBatch[]> {
    // remove batch, metadata data
    this.visibleDocumentsService!.removeBatches(batchUuids);
    // console.debug(`Removed ${batchUuids.length} Stale Batches`);
    batchUuids.forEach((x) =>
      AcceleratorBatchDocumentLoader.removeBatchData(x),
    );

    // load batches
    const accBatchPromises = BatchService.loadBatches(
      batchUuids,
      this.state.batchClassConfig!.batchClassId,
    );
    const accBatches = await accBatchPromises;
    return accBatches;
  }
  async reloadStaleBatches(saveModel: boolean) {
    if (saveModel) {
      await this.saveModel();
    }
    this.setStateSync({
      status: "loading_bis",
    });
    const batchClassId = getObjectStoreIdString(this.state.batchClassConfig);
    // unlock all models
    const staleVisibleDocs = this.visibleDocumentsService
      .getAllDocumentMetadata()
      .filter((x) => x.stale);
    // get unchanged models on these stale docs
    const changedModelsOnStaleDocs = staleVisibleDocs
      .filter((x) => x.hasModelChanged)
      .map((x) => x.guessModelStoreKey());
    // remove changed models on stale docs
    const removedModels = changedModelsOnStaleDocs.map((x) =>
      ModelStore.remove(x),
    );
    const modelUnlocks = removedModels.map((x) =>
      this.props.unlock(x?.uuid ?? "", batchClassId, ""),
    );

    // remove all batches
    const uuids = this.visibleDocumentsService
      .getAllBatches()
      .map((x) => x.uuid);
    const metadata = this.visibleDocumentsService
      .getAllBatches()
      .flatMap((x) => x.metadata);
    this.visibleDocumentsService.removeBatches(uuids);
    this.visibleDocumentsService.setBatchFilter("");
    uuids.forEach((uuid) =>
      AcceleratorBatchDocumentLoader.removeBatchData(uuid),
    );
    for (const doc of metadata) {
      try {
        const model = AcceleratorDocModelStore.getModel(doc)?.storeId;
        if (model) {
          AcceleratorDocModelStore.removeAssociation(doc.storeId, model);
        }
      } catch (e) {}
    }

    await this.loadBatchInstances();
    await Promise.all(modelUnlocks);
    if (this.visibleDocumentsService.getAllBatches().length > 0) {
      await this.changeDocument(
        this.visibleDocumentsService.getAllBatches()[0].metadata[0].storeId,
      );
    } else {
      this.changeDocument({ batchInstanceUuid: "", docId: "" });
    }
  }

  async saveModel() {
    // wait for any in-flight priority/collateral extraction previews (kicked off by
    // updateModel() on field edits) to settle, so SaveModel.run() never reads
    // AcceleratorDocStore/AcceleratorDocMetadataStore mid-mutation.
    await Promise.all(this.pendingExtractions);

    this.setStateSync({
      statusPromiseCount: 0,
      statusPromiseTarget: 0,
    });

    // Called when each model save request is finished
    const modelSaveCb = (total: number) => {
      if (total === -1) {
        this.setStateSync((s) => ({
          statusPromiseCount: s.statusPromiseCount + 1,
        }));
      } else {
        this.setStateSync((s) => ({
          statusPromiseLabel: `Saving Template${total > 1 ? "s" : ""}`,
          statusPromiseTarget: total,
          statusPromiseCount: 0,
        }));
      }
    };

    this.setStateSync({
      status: "saving_models",
    });

    const modelSaveResults = await new SaveModel(
      AcceleratorDocStore,
      AcceleratorDocModelStore,
      ModelStore,
      AcceleratorDocMetadataStore,
      this.context?.username,
    ).run(
      modelSaveCb,
      this.props.unlock,
      this.props.lockedBy,
      this.state.debug_reprocessAffectedBatches,
      this.state.document!._storeId,
    );

    if (modelSaveResults.length === 0) {
      return this.setStateSync({
        status: "loaded",
      });
    }

    const successfullySaved = modelSaveResults.filter(
      (x) => x.result.successful,
    ).length;
    if (successfullySaved != 0) {
      this.props.modelSaveSuccessAlert(modelSaveResults);
    }
    const failedSaved = modelSaveResults.filter(
      (x) => x.result.successful == false,
    ).length;
    if (failedSaved != 0) {
      this.props.modelSaveFailureAlert(modelSaveResults);
    }
    // determine what error message to report
    const failedSavedModels = modelSaveResults.filter(
      (x) => !x.result.successful,
    );
    if (failedSavedModels.length !== 0) {
      eventEmitter.publish(
        new DialogBoxConfirmEvent(
          SaveModelMessages.errorSaving(failedSavedModels.length),
          () => {},
        ),
      );
    }

    const noModelPages = modelSaveResults.filter(
      (x) => x.result.successful && !x.result.modelPages,
    );

    if (noModelPages.length !== 0) {
      this.props.modelPagesFailedAlert(noModelPages);
    }

    const currDocId = this.state.document!._storeId as AcceleratorDocStoreKey;
    const currBatchFilter = this.visibleDocumentsService.getBatchFilter();
    // distinct list of affected batches by UUID
    const affectedBatches = modelSaveResults
      .map((x) => x.modelSaveData.batchInstanceUuid)
      .concat(modelSaveResults.flatMap((x) => x.result.affectedBatches))
      .concat(currDocId.batchInstanceUuid)
      .filter((x) => x != undefined && x !== "")
      .filter((x, i, a) => a.indexOf(x) === i);

    if (this.state.debug_reprocessAffectedBatches) {
      console.debug(
        `Reprocessing ${affectedBatches.length} affected batches enabled`,
      );
      // trigger reprocess
      const accBatches = await this.reprocessBatches(affectedBatches, true);
      this.visibleDocumentsService.addBatches(accBatches);
      this.visibleDocumentsService.setSearchedBatches(null);
    }

    // we now need to reload these Batch Instances. the BatchInstanceStore should be checked here
    this.setStateSync(
      {
        status: "loaded",
        statusPromiseLabel: undefined,
        statusPromiseCount: 0,
        statusPromiseTarget: 0,
        // hopefully current doc still exists
      },
      () => {
        // check if old batch still exists after reload
        const batch = this.visibleDocumentsService
          .getBatches()
          .find((x) => x.uuid === currDocId.batchInstanceUuid);
        if (
          currBatchFilter !== SELECT_ALL_BATCH_INSTANCES &&
          batch != undefined
        ) {
          // change to old Doc if it still exists, otherwise
          if (
            batch.metadata.find((x) => compareAccDocKey(x.storeId, currDocId))
          ) {
            this.changeDocument(currDocId, undefined);
          } else {
            this.changeBatchInstance(currDocId.batchInstanceUuid);
          }
        } else {
          // otherwise assign to ALL
          this.changeBatchInstance(SELECT_ALL_BATCH_INSTANCES);
        }
      },
    );
  }

  componentWillUnmount() {
    // if (this.appState.documentViewerWindow != null)
    // this.appState.documentViewerWindow.close();
    if (this.unsubscribeHashChanges) {
      this.unsubscribeHashChanges();
    }
  }

  async handleNewModelVersion(update: boolean, model?: Model) {
    // console.log(`AppNew.handleNewModelVersion(${update})`);
    if (!model) {
      return;
    }
    let updatedModel = model;
    if (update) {
      this.setStateSync({
        status: "refreshing_model",
        statusPromiseLabel: "Refreshing Template",
      });
      // trigger model reload - how ?
      try {
        // lookup Model
        const loadedModel = await ModelStore.lookup(model.storeId);
        if (loadedModel == undefined) {
          // yuck !
          throw new Error("Undefined returned from ModelStore.lookup");
        }
        updatedModel = loadedModel;
        // update Model, don't mark as changed
      } catch (e) {
        console.error("Error looking up Model for refresh", e);
        // trigger dialog error ?
      }
    } else {
      console.debug(
        `Ignoring v${model.knownHigherVersion} for Model ${getObjectStoreIdString(model)}`,
      );
      updatedModel.setVersion(model.knownHigherVersion ?? model.version);
    }
    this.updateModel(updatedModel, this.state.document!, false);
    this.setStateSync({
      status: "loaded",
    });
  }

  render() {
    const loadingSomething: boolean =
      [
        "refreshing_model",
        "saving_models",
        "loading_bis",
        "loading_bccs",
        "reprocessing_batch_instance",
        "deleting_batch_instance",
      ].indexOf(this.state.status) > -1;
    const loadingBccs = this.state.status === "loading_bccs";
    const hasBccs = !loadingBccs && this.state.batchClassConfigs.length != 0;
    // is this not a given ?
    const bccSelected = hasBccs && this.state.batchClassConfig != undefined;
    // TODO: get a list of BatchInstance promises ??
    const loadingBatchInstances = this.state.status === "loading_bis";

    const document = this.state.document; //|| HydratableFactory.getAcceleratorDoc(async () => undefined,
    // { batchInstanceUuid: '', docId: '' });
    const docKey = this.state.document?._storeId ?? {
      batchInstanceUuid: "",
      docId: "",
    };
    const model = this.state.model; //|| HydratableFactory.getModel(async () => undefined,
    // { batchClassIdentifier: '', name: '' });
    const modelKey = this.state.model?._storeId ?? {
      batchClassIdentifier: "",
      name: "",
    };
    if (document && !model) {
      console.error(
        `Model not defined for ${getObjectStoreIdString(document)} in AppNew render.`,
      );
    }

    const visibleDocumentsMetadata: AcceleratorDocMetadata[] =
      this.visibleDocumentsService?.getVisibleDocumentMetadata() ?? [];
    const visibleDocMetadata =
      this.visibleDocumentsService?.getAcceleratorDocMetadata();

    // count DocumentStatuses
    // TODO: move into ConfigUiVisibleDocumentsService
    const documentStatuses: { [x: string]: number } =
      this.visibleDocumentsService.getVisibleDocStatuses();

    const reloadDocOnHydrationKey =
      stringifyKey(docKey) + document?.isHydrated(false);
    const reloadModelOnHydrationKey =
      stringifyKey(modelKey) +
      model?.isHydrated(false) +
      (model?.isHydrated(false) ? model._hydrated!.docType : "");
    const reloadDocAndModelOnHydrationKey =
      reloadDocOnHydrationKey + "" + reloadModelOnHydrationKey;
    const stableReloadDocAndModelKey =
      (document ? document._storeId.docId + document._storeId.batchInstanceUuid : "") +
      (model ? model._storeId.batchClassIdentifier + model._storeId.name : "") +
      (model?.isHydrated(false) ? model._hydrated!.docType : "");

    return (
      <LoginForm
        version={this.props.version}
        loggedIn={() =>
          this.setState(
            {
              loggedIn: true,
            },
            () => this.bootstrap(),
          )
        }
      >
        <SnackbarProvider />
        <div className={"appBody"}>
          <CrudActionBar
            status={this.state.status}
            batchClassId={this.state.batchClassConfig?.batchClassId ?? ""}
            batchClassConfigs={this.state.batchClassConfigs}
            batchClassConfig={this.state.batchClassConfig}
            changeBcc={(bcc) => {
              if (
                bcc.batchClassId !== this.state.batchClassConfig?.batchClassId
              ) {
                this.changeBatchClassConfig(bcc);
              }
            }}
            logout={async () => {
              await Promise.all(this.props.unlockAll());
              new Authenticator().logout();
            }}
            itemSelected={(item) => {
              const page = itemToPageMap[item];
              const bccId = this.state.batchClassConfig?.batchClassId ?? "";
              window.location.href = `template-management.html?page=${page}#/${bccId}`;
            }}
          />
          {/* <ActionBar items={actionBarItems} /> */}
          {/* Handles loading indicator when saving Models, deleting/reprocessing Batch Instances */}
          {loadingSomething && (
            <PageLoading
              indeterminateWhenZero={true}
              label={this.state.statusPromiseLabel}
              count={this.state.statusPromiseCount}
              target={this.state.statusPromiseTarget}
            />
          )}
          {!loadingSomething && !hasBccs && <NoBatchClasses />}
          {!loadingSomething && bccSelected && (
            <div className={"wrapper col-2"}>
              <SpanHighlightProvider>
                <HighlightProvider>
                  <PageProvider>
                    <div>
                      {!this.state.hidePageCountWarning && (
                        <PageCountWarning
                          onConfirm={() =>
                            this.setStateSync({
                              hidePageCountWarning: true,
                            })
                          }
                        />
                      )}
                      {!this.state.hideExpirationWarning && (
                        <LicenseWarning
                          onConfirm={() =>
                            this.setStateSync({
                              hideExpirationWarning: true,
                            })
                          }
                        />
                      )}
                      {/* Left column */}
                      <StaleModel
                        model={this.state.model?._hydrated}
                        onConfirm={(reload) =>
                          this.handleNewModelVersion(
                            reload,
                            this.state.model?.getHydrated(),
                          )
                        }
                      />
                      {/* <StaleAcceleratorBatch metadata={visibleDocumentsMetadata.find(x => compareAccDocKey(x.storeId, visibleDocMetadata?.storeId))}
                                            onConfirm={saveModel => this.reloadStaleBatches(saveModel)} /> */}
                      <StaleAcceleratorBatch
                        key={
                          "STALEBATCH" +
                          reloadDocAndModelOnHydrationKey +
                          Math.random()
                        }
                        metadata={visibleDocMetadata}
                        onConfirm={(saveModel) =>
                          this.reloadStaleBatches(saveModel)
                        }
                      />

                      <TooltipPanel
                        title={"Classification"}
                        open
                        sticky
                        tooltipText={TTClassificationPanel}
                      >
                        <DocumentClassification
                          key={"DC" + stableReloadDocAndModelKey}
                          acceleratorDoc={document}
                          docTypes={this.state.batchClassConfig!.docTypes}
                          model={model}
                          modelChanged={(modified: Model) =>
                            this.updateModel(modified, document!)
                          }
                          modelNameChanged={(
                            newName: string,
                            modified: Model,
                          ) => {
                            this.updateModelName(newName, modified, document!);
                          }}
                          documentStale={() => this.forceUpdate()}
                          activeStartRuleIndex={this.state.activeStartRuleIndex}
                          setActiveStartRuleIndex={(index) =>
                            this.setStateSync({ activeStartRuleIndex: index })
                          }
                        />
                      </TooltipPanel>

                      <ModelConfigurationViewToolTipPanel
                        title={"Document Level Fields"}
                        open
                        sticky
                        tooltipText={TTDlfPanel}
                        dropdownTooltipText={TTDlfPanelDropdown}
                      >
                        <DlfEditorList
                          key={"DLF" + stableReloadDocAndModelKey}
                          acceleratorDoc={document}
                          docTypeConfig={
                            model && model.isHydrated(false)
                              ? this.state.batchClassConfig!.getDocumentType(
                                  model.getHydrated().docType ?? "",
                                )!
                              : DocTypeConfig.getUnknown()
                          }
                          model={model!}
                          modelChanged={(modified: Model) => {
                            console.log(document?._storeId);
                            this.updateModel(modified, document!);
                          }}
                          // set by panel
                          view={"extraction"}
                          hideHighlights={this.state.activeStartRuleIndex !== null}
                        />
                      </ModelConfigurationViewToolTipPanel>

                      <ModelConfigurationViewToolTipPanel
                        title={"Tables"}
                        tooltipText={TTTablePanel}
                        shortTooltip
                        dropdownTooltipText={TTTablePanelDropdown}
                        open
                        sticky
                      >
                        <TableEditor
                          key={"TABLE" + stableReloadDocAndModelKey}
                          view={"extraction"}
                          acceleratorDoc={document}
                          model={model!}
                          docTypeConfig={
                            model && model.isHydrated(false)
                              ? this.state.batchClassConfig!.getDocumentType(
                                  model.getHydrated().docType ?? "",
                                )!
                              : DocTypeConfig.getUnknown()
                          }
                          changeToReuse={(
                            tableName: string,
                            reuseIndex: number,
                            skipModelChanged?: boolean,
                          ) => {
                            // also need Field Name
                            if (!document || !model) return;
                            const extractedTableData = document
                              .getHydrated()
                              .getExtractedTableData(tableName);
                            if (!extractedTableData) {
                              console.debug(
                                "Could not process changeToReuse() request for table " +
                                  tableName,
                              );
                              return;
                            }

                            const wasChanged = model.getHydrated().changed;
                            const oldTableModel = model
                              .getHydrated()
                              .getTableModel(tableName);

                            let table =
                              extractedTableData.getReuseAt(reuseIndex);
                            if (!table && reuseIndex !== 0) {
                              console.log(
                                `Could not find Reuse at Index ${reuseIndex} for TABLE:${tableName}`,
                              );
                              return;
                            } else if (!table && reuseIndex === 0) {
                              console.log(
                                `Could not find Reuse at Index ${reuseIndex} for TABLE:${tableName}`,
                              );
                              table =
                                extractedTableData.getExtracted()
                                  ?.tableTemplateData!;
                            }

                            // get matcher data from baseField
                            const newTableModel = new TableModel(table!);

                            // determine if tables are the same
                            if (
                              oldTableModel?.getModelId() ===
                              newTableModel.getModelId()
                            ) {
                              console.log(
                                "not setting model.changed=true when reuse tableModel already set on model",
                              );
                              return this.updateModel(
                                model.getHydrated(),
                                document!,
                                wasChanged,
                                true,
                              );
                            }

                            model.getHydrated().setTableModel(newTableModel);
                            extractedTableData.setIndex(reuseIndex);

                            if (
                              model.getHydrated().unknown &&
                              skipModelChanged
                            ) {
                              console.log(
                                `Unknown model with changed=${wasChanged} had table reuse set on mount`,
                              );
                              model.getHydrated().setChanged(wasChanged);
                              this.updateModel(
                                model.getHydrated(),
                                document!,
                                wasChanged,
                                true,
                              );
                              // this should call this.updateModel() here !
                            } else {
                              console.log(
                                `model with changed=${wasChanged} is changed=${
                                  skipModelChanged ? wasChanged : true
                                } after generating reuse`,
                              );
                              this.updateModel(
                                model.getHydrated(),
                                document!,
                                skipModelChanged ? wasChanged : true,
                                true,
                              );
                            }
                          }}
                          modelChanged={(modified: TableModel) => {
                            //console.log(`Table Model ${modified.name} modified`);
                            model!.getHydrated().setTableModel(modified);
                            this.updateModel(model!.getHydrated(), document!);
                          }}
                        />
                      </ModelConfigurationViewToolTipPanel>

                      <ModelNotesPanel
                        key={reloadModelOnHydrationKey}
                        model={model}
                        modelChanged={(modified) =>
                          this.updateModel(modified, document!)
                        }
                      />

                      {/* Continually updates Lock Data within LockContext. */}
                      {this.state.batchClassConfig && (
                        <>
                          <BatchLockInfoTimer
                            intervalMs={LOCK_TIMEOUT_MS}
                            batchClassId={
                              this.state.batchClassConfig.batchClassId
                            }
                            debug={this.state.debug_showDebugTools}
                          />
                          <ModelLockInfoTimer
                            intervalMs={LOCK_TIMEOUT_MS}
                            batchClassId={
                              this.state.batchClassConfig.batchClassId
                            }
                            debug={this.state.debug_showDebugTools}
                          />
                        </>
                      )}
                    </div>
                    {/* Right column */}
                    <div>
                      <BatchListPanel
                        docTypes={this.state.batchClassConfig?.docTypes ?? []}
                        // FIXME: this is bad ! add a new getter, or something else ?
                        allBatches={
                          (
                            this
                              .visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
                          ).currentBatches
                        }
                        defaultSearchTerm={this.visibleDocumentsService.getLastSearchFilter()}
                        availableBatches={this.visibleDocumentsService.getBatches()}
                        batchFilter={this.visibleDocumentsService.getBatchFilter()}
                        visibleBatches={this.visibleDocumentsService.getVisibleBatches()}
                        searchedBatches={(searched, filter) =>
                          this.searchBatch(searched, filter)
                        }
                        changeBatch={(uuid) => this.changeBatchInstance(uuid)}
                        saveModel={() => this.saveModel()}
                        reprocessBatches={(uuids) => {
                          if (uuids.includes(SELECT_ALL_BATCH_INSTANCES)) {
                            this.reprocessBatchInstances(
                              this.visibleDocumentsService
                                .getVisibleBatches()
                                .map((x) => x.uuid),
                            );
                          } else {
                            this.reprocessBatchInstances(uuids);
                          }
                        }}
                        deleteBatches={(uuids) => {
                          if (uuids.includes(SELECT_ALL_BATCH_INSTANCES)) {
                            this.deleteBatchInstances(
                              this.visibleDocumentsService
                                .getVisibleBatches()
                                .map((x) => x.uuid),
                            );
                          } else {
                            this.deleteBatchInstances(uuids);
                          }
                        }}
                      >
                        {(
                          selectedBatches,
                          isAllSelected,
                          toggleBatchSelected,
                          toggleAllBatchesSelected,
                          collapsedBatches,
                          isAllCollapsed,
                          toggleBatchCollapsed,
                          toggleAllCollapsed,
                        ) => {
                          return (
                            //  TODO: this component requires DocSelectorItems to be rendered before the active Doc changes
                            <DocumentSelectorList
                              filtersCount={documentStatuses}
                              toggleSelectAll={(val, filter) =>
                                toggleAllBatchesSelected(val, filter)
                              }
                              toggleCollapseAll={(val) =>
                                toggleAllCollapsed(val)
                              }
                              collapsedBatches={collapsedBatches}
                              selectedBatches={selectedBatches}
                              isAllCollapsed={isAllCollapsed}
                              changeFilter={(filter?: DocumentStatus) => {
                                // TOOD: check should be performed during BatchInstance change, otherwise invisible doc selected
                                // check if active AcceleratorDoc is visible !
                                const selectedDocVisible =
                                  this.visibleDocumentsService.isDocVisible(
                                    this.state.document?._storeId as
                                      | AcceleratorDocStoreKey
                                      | undefined,
                                    filter,
                                  );

                                if (selectedDocVisible) return;
                                // TODO: log time for changeDocument to complete.
                                const filteredMetadata =
                                  this.visibleDocumentsService
                                    .getVisibleDocumentMetadata()
                                    .filter((x) =>
                                      this.visibleDocumentsService.metadataHasStatus(
                                        x,
                                        filter,
                                      ),
                                    );
                                if (filteredMetadata.length > 0) {
                                  this.changeDocument(
                                    filteredMetadata[0].storeId,
                                  );
                                } else {
                                  this.hideDocument();
                                }
                              }}
                              metadata={visibleDocumentsMetadata}
                              renderGroup={(
                                metadata,
                                lockedBy,
                                groupLength,
                              ) => (
                                <BatchGroup
                                  batchUuid={metadata.storeId.batchInstanceUuid}
                                  batchInstanceId={metadata.batchInstanceId}
                                  batchName={metadata.batchName}
                                  source={metadata.source}
                                  lockedBy={lockedBy}
                                  status={""}
                                  collapsed={collapsedBatches.includes(
                                    metadata.storeId.batchInstanceUuid,
                                  )}
                                  collapse={() =>
                                    toggleBatchCollapsed(
                                      metadata.storeId.batchInstanceUuid,
                                    )
                                  }
                                  // show checkbox if more than one group exists
                                  checkbox={
                                    groupLength > 1 ? (
                                      <AdvancedTooltip
                                        tooltipText={TTSelectBatch}
                                      >
                                        {(tProps) => (
                                          <input
                                            {...tProps}
                                            type={"checkbox"}
                                            checked={selectedBatches.includes(
                                              metadata.storeId
                                                .batchInstanceUuid,
                                            )}
                                            onChange={() =>
                                              toggleBatchSelected(
                                                metadata.storeId
                                                  .batchInstanceUuid,
                                              )
                                            }
                                          />
                                        )}
                                      </AdvancedTooltip>
                                    ) : undefined
                                  }
                                />
                              )}
                              renderItem={(metadata) => (
                                <DocumentSelectorItem
                                  key={stringifyKey(metadata.storeId)}
                                  acceleratorDocKey={metadata.storeId}
                                  selectedAccDocKey={
                                    this.state.document?._storeId as
                                      | AcceleratorDocStoreKey
                                      | undefined
                                  }
                                  stale={metadata.stale}
                                  loadingError={metadata.loadingError}
                                  pages={metadata.pageCount}
                                  statuses={metadata.statuses}
                                  modelName={metadata.classifiedModelName}
                                  docType={metadata.classifiedDocType}
                                  modelChanged={metadata.hasModelChanged}
                                  onClick={() =>
                                    this.changeDocument(metadata.storeId)
                                  }
                                />
                              )}
                            />
                          );
                        }}
                      </BatchListPanel>

                      <DocumentImageViewer
                        key={`DIV ${reloadDocOnHydrationKey}`}
                        acceleratorDoc={document}
                        setSpanOutline={
                          window.fssdebug.accelerator.highlightSpans
                        }
                        showSpanOutline={this.state.debug_highlightSpans}
                      />

                      <CollapsiblePanel
                        style={{ marginTop: 25 }}
                        title="Document Metadata"
                        headerChildren={
                          <div>
                            <h4>{`Version: ${this.props.version}`}</h4>
                          </div>
                        }
                        sticky
                        open
                      >
                        <DocumentMetadata
                          key={
                            "META" +
                            reloadDocOnHydrationKey +
                            (this.state.document ? "visbile" : "invisible")
                          }
                          acceleratorDocMetadata={
                            this.state.document ? visibleDocMetadata : undefined
                          }
                          batchClassId={
                            this.state.batchClassConfig!.batchClassId
                          }
                          debugView={this.state.debug_showDebugTools}
                        />
                      </CollapsiblePanel>
                      {this.state.debug_showDebugTools && (
                        <AppDebug
                          model={model}
                          document={document}
                          visibleDocMetadata={visibleDocMetadata}
                          visibleDocumentsService={
                            this
                              .visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
                          }
                          authContext={this.context}
                          debug_highlightSpans={this.state.debug_highlightSpans}
                          forceUpdate={this.forceUpdate.bind(this)}
                          debug_failLockAcquisitions={
                            this.state.debug_failLockAcquisitions
                          }
                          setDebug={(x, v) => {
                            const debugState = {
                              [x]: v,
                            };
                            this.setStateSync(debugState);
                          }}
                          updateModel={this.updateModel.bind(this)}
                          {...this.props}
                        />
                      )}
                    </div>
                  </PageProvider>
                </HighlightProvider>
              </SpanHighlightProvider>
            </div>
          )}
        </div>
        <DialogBoxHandler />
      </LoginForm>
    );
  }
}

export default AppNew;

type DebugCmds = "debug_failLockAcquisitions";
type SetDebug = (name: DebugCmds, value: boolean) => void;
type AppDebugProps = {
  debug_failLockAcquisitions: boolean;
  debug_highlightSpans: boolean;
  authContext: React.ContextType<typeof UserDetailsStateContext>;
  visibleDocMetadata: AcceleratorDocMetadata | undefined;
  document: Hydratable<AcceleratorDoc, AcceleratorDocStoreKey> | undefined;
  model: Hydratable<Model, ModelStoreKey> | undefined;
  setDebug: SetDebug;
  visibleDocumentsService: ConfigUiVisibleDocumentsServiceImpl;
  updateModel: AppNew["updateModel"];
  forceUpdate: VoidFunction;
  deleteFailedAlert: BooleanAlert;
  deleteSuccessAlert: BooleanAlert;
  reprocessFailedAlert: BooleanAlert;
  reprocessSuccessAlert: BooleanAlert;
  modelPagesFailedAlert: ModelSaveAlert;
  modelSaveSuccessAlert: ModelSaveAlert;
  modelSaveFailureAlert: ModelSaveAlert;
};
const AppDebug = (props: AppDebugProps) => {
  return (
    <>
      <p>Logged In: {props.authContext?.username}</p>
      {/* simulate a Document Start Rule failing extraction */}
      <Button
        onClick={() => {
          AcceleratorDocMetadataStore.markAllDocMetadataStale(
            props.document!._storeId.batchInstanceUuid,
          );
        }}
      >
        Simulate DLF Extraction fail
      </Button>
      {/* Cause lock acquisitions to fail */}
      <OnOffToggle
        active={props.debug_failLockAcquisitions}
        onClick={() => {
          props.setDebug(
            "debug_failLockAcquisitions",
            !props.debug_failLockAcquisitions,
          );
        }}
      >
        Fail All Lock Acquisitions
      </OnOffToggle>
      {/* Make document stale */}
      {props.visibleDocMetadata && (
        <OnOffToggle
          active={props.visibleDocMetadata.stale ?? false}
          onClick={() => {
            (
              props.visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
            )._wipeAcceleratorDocMetadataCache();
            AcceleratorDocMetadataStore.markAllDocMetadataStale(
              props.visibleDocMetadata!.storeId.batchInstanceUuid,
              !props.visibleDocMetadata!.stale,
            );
            props.forceUpdate();
          }}
        >
          Toggle Document Stale
        </OnOffToggle>
      )}
      {/* Make document error */}
      {props.visibleDocMetadata && (
        <OnOffToggle
          active={props.visibleDocMetadata.loadingError}
          onClick={() => {
            (
              props.visibleDocumentsService as ConfigUiVisibleDocumentsServiceImpl
            )._wipeAcceleratorDocMetadataCache();
            AcceleratorDocMetadataStore.markAllDocMetadataErrorLoading(
              props.visibleDocMetadata!.storeId.batchInstanceUuid,
              !props.visibleDocMetadata!.loadingError,
            );
            props.forceUpdate();
          }}
        >
          Toggle Document Loading Error
        </OnOffToggle>
      )}
      {/* Make Model stale */}
      {props.model?.isHydrated(false) && (
        <OnOffToggle
          active={props.model!.getHydrated().stale}
          onClick={() => {
            props.model!.getHydrated().knownHigherVersion =
              (props.model?.getHydrated().version ?? 0) + 1;
            props.updateModel(
              props.model!.getHydrated(),
              props.document!,
              props.model?.getHydrated().changed,
            );
            props.forceUpdate();
          }}
        >
          Toggle Model Stale
        </OnOffToggle>
      )}
      {/* Notification buttons */}
      <Button
        onClick={() =>
          props.modelPagesFailedAlert([
            {
              modelSaveData: undefined,
              result: {
                successful: true,
                modelPages: undefined,
                model: { name: "MODEL_NAME" },
              },
            },
          ] as unknown as SaveModelResult[])
        }
      >
        Send Model Pages Save Error
      </Button>
      <Button
        onClick={() =>
          props.modelSaveFailureAlert([
            {
              modelSaveData: undefined,
              result: {
                successful: false,
                modelPages: undefined,
              },
            },
          ] as unknown as SaveModelResult[])
        }
      >
        Send Model Save Error
      </Button>
      <Button
        onClick={() =>
          props.modelSaveSuccessAlert([
            {
              modelSaveData: undefined,
              result: { successful: true, modelPages: undefined },
            },
          ] as unknown as SaveModelResult[])
        }
      >
        Send Model Save Success
      </Button>
      <Button onClick={() => props.deleteFailedAlert([false])}>
        Send Delete Error
      </Button>
      <Button onClick={() => props.deleteSuccessAlert([true])}>
        Send Delete Success
      </Button>
      <Button onClick={() => props.reprocessFailedAlert([false])}>
        Send Reprocess Error
      </Button>
      <Button onClick={() => props.reprocessSuccessAlert([true])}>
        Send Reprocess Success
      </Button>

      <CollapsiblePanel open={false} title={"Locks"} style={{ marginTop: 25 }}>
        <LockDebugger />
      </CollapsiblePanel>
      <CollapsiblePanel
        open={false}
        title={"Model Debugger"}
        style={{ marginTop: 25 }}
      >
        <div>
          {props.model != null && (
            <pre>{JSON.stringify(props.model, null, 2)}</pre>
          )}
        </div>
      </CollapsiblePanel>
      <CollapsiblePanel
        open={false}
        title={"Document Debugger"}
        style={{ marginTop: 25 }}
      >
        <div>
          {props.document != null && (
            <pre>{JSON.stringify(props.document, null, 2)}</pre>
          )}
        </div>
      </CollapsiblePanel>

      <CollapsiblePanel
        open={false}
        title={"EFD Debugger"}
        style={{ marginTop: 25 }}
      >
        <EfdDebugger acceleratorDoc={props.document} />
      </CollapsiblePanel>
      <CollapsiblePanel
        open={false}
        title={"ETD Debugger"}
        style={{ marginTop: 25 }}
      >
        <EtdDebugger acceleratorDoc={props.document} />
      </CollapsiblePanel>
      <CollapsiblePanel
        open={false}
        title={"Association Debugger"}
        style={{ marginTop: 25 }}
      >
        <DocModelAssociationDebugger
          batchClassId={props.model?._storeId.batchClassIdentifier ?? ""}
          intervalMs={2000}
          currentBatchUuid={props.document?._storeId.batchInstanceUuid ?? ""}
        />
      </CollapsiblePanel>
    </>
  );
};
